import React, { useContext, useRef, useState, useEffect } from "react";
import {
  Drawer,
  Row,
  Col,
  Typography,
  Form,
  Input,
  Button,
  Select,
  message,
  InputNumber,
  Alert,
  Space,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import btnStyles from "../../styles/Custom/Button.module.css";
import { CloseOutlined } from "@ant-design/icons";
import inputStyles from "../../styles/Custom/Input.module.css";
import { validateConfig } from "../../config/validate.config";
import { UserContext } from "../../context/UserContext";
import { getCookie } from "../../helpers/localStorage";
import { FacilityID } from "../../constants/defaultKeys";
import {
  addPatientApi,
  getPatientDetailsApi,
  updatePatientApi,
} from "../../services/receptionist.services";
import { contactRelationOptions, genderKeys } from "./receptionKeys";
import PatientMobileSearch from "./PatientMobileSearch";
import useIsMobile from "../../hooks/useIsMobile";
import useReceptionistMobilePickerProps from "./receptionistMobilePickerProps";
import "./receptionistMobileForms.css";

const AddPatientModal = ({ open, setOpen, editData, refresh }) => {
  const isMobile = useIsMobile();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { userDetails } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [searchedText, setSearchedText] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [patientDetails, setPatientDetails] = useState();
   function disabledDate(current) {
      return current && current > dayjs().endOf("day");
    }
  useEffect(() => {
  if (editData?.id) {
    form.setFieldsValue({
      ...editData,
      dob: editData?.dob ? dayjs(editData.dob) : null,   
    });
  }
}, [editData?.id]);

useEffect(() => {
  form.setFieldValue("age", null);
}, [selectedId, editData?.id]);

  useEffect(() => {
    if (selectedId) {
      getPatientDetailsApi(selectedId)
        .then((res) => {
          form?.setFieldsValue({
            mobile: res?.data?.mobile,
          });
          setPatientDetails(res?.data);
          // setSearchedText(res?.data?.mobile);
          // setFacilityId(res?.data?.facility);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [selectedId]);

  const onFinish = (values) => {
    setLoading(true);
  
    let calculatedDOB = null;
    if (values?.dob) {
      calculatedDOB = dayjs(values.dob).format("YYYY-MM-DD");
    }
  
    if (values?.age && !values?.dob) {
      const year = dayjs().year() - values.age;
      calculatedDOB = `${year}-01-01`;
    }
  
    const formData = {
      ...values,
      dob: calculatedDOB,
      facility: Number(getCookie(FacilityID)) || userDetails?.currFacility,
    };
  
    delete formData.age; 
  
    if (editData?.id) {
      updatePatientApi(editData?.id, formData)
        .then((res) => {
          message.success(res?.data?.message);
          setOpen(false);
          setLoading(false);
          refresh();
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          setLoading(false);
        });
    } else {
      addPatientApi(formData)
        .then((res) => {
          message.success(res?.data?.message);
          setOpen(false);
          setLoading(false);
          refresh();
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          setLoading(false);
        });
    }
  };

  return (
    <div>
      <Drawer
        width={isMobile ? "100%" : "40%"}
        rootClassName={isMobile ? "reception-drawer-fullscreen" : undefined}
        open={open}
        title={
          isMobile ? (
            <div className="reception-drawer-mobile-title">
              <Typography
                className="reception-drawer-mobile-title-text"
                style={{
                  fontWeight: "400",
                  fontSize: "20px",
                }}
              >
                {editData ? "Edit Basic Details" : "Add Patient Details"}
              </Typography>
              <CloseOutlined onClick={() => setOpen(false)} />
            </div>
          ) : (
            <Row>
              <Col span={12} className="flex-start">
                <Typography
                  style={{
                    marginLeft: "12px",
                    fontWeight: "400",
                    fontSize: "20px",
                  }}
                >
                  {editData ? "Edit Basic Details" : "Add Patient Details"}
                </Typography>
              </Col>
              <Col span={12} className="flex-end">
                <CloseOutlined onClick={() => setOpen(false)} />
              </Col>
            </Row>
          )
        }
        footer={
          isMobile ? (
            <div className="reception-drawer-footer-row">
              <Button
                type="primary"
                ghost
                className={btnStyles.mdBtn}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                loading={loading}
                type="primary"
                className={btnStyles.mdBtn}
                onClick={() => {
                  if (formRef.current) {
                    formRef.current.submit();
                  }
                }}
              >
                {editData ? "Save" : "Submit"}
              </Button>
            </div>
          ) : (
            <div className="flex-end" style={{ gap: "24px" }}>
              <Form.Item>
                <Button
                  type="primary"
                  ghost
                  className={btnStyles.mdBtn}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
              </Form.Item>
              <Form.Item>
                <Button
                  loading={loading}
                  htmlType="submit"
                  type="primary"
                  className={btnStyles.mdBtn}
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.submit();
                    }
                  }}
                >
                  {editData ? "Save" : "Submit"}
                </Button>
              </Form.Item>
            </div>
          )
        }
        onClose={() => setOpen(false)}
        closeIcon={null}
      >
        <Form
          layout="vertical"
          // requiredMark={false}
          onFinish={onFinish}
          form={form}
          ref={formRef}
        >
          <PatientMobileSearch
            searchedText={searchedText}
            setSearchedText={setSearchedText}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            form={form}
          />
          {selectedId && !editData?.id && (
            <Alert
              type="info"
              showIcon
              message={`
              A patient named ${
                patientDetails?.first_name + " " + patientDetails?.last_name
              } is already
               registered with this mobile number. If you would like to add a new patient with the same number, 
               please continue and select the relationship to the existing patient.`}
            ></Alert>
          )}
          {selectedId && !editData?.id && (
            <Form.Item label="Relation with existing patient" name="relation">
              <Select
                options={contactRelationOptions}
                style={{ width: "300px" }}
                className={inputStyles.mdSelect}
              />
            </Form.Item>
          )}
          <Space>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={validateConfig.isRequired("First Name")}
            >
              <Input
                className={inputStyles.mdInput}
                style={{ width: "180px" }}
                placeholder="Enter first name"
              />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="last_name"
              // rules={validateConfig.isRequired("Last Name")}
            >
              <Input
                className={inputStyles.mdInput}
                style={{ width: "180px" }}
                placeholder="Enter first name"
              />
            </Form.Item>
          </Space>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Date Of Birth"
                name="dob"
                rules={[{ required: true, message: "Enter DOB or Age" }]}
              >
                <DatePicker
                  {...mobilePickerProps}
                  disabledDate={disabledDate}
                  className={inputStyles.mdDateInput}
                  style={{ width: "100%" }} 
                  placeholder="Select DOB"
                  onChange={(date) => {
                    if (date) {
                      const today = dayjs();
                      const age = today.diff(date, "year");
                      form.setFieldValue("age_by_dob", age);
                    } else {
                      form.setFieldValue("age_by_dob", null);
                    }
                  }}
                />
              </Form.Item>
            </Col>          

            <Col span={4}>
              <Form.Item
                label="Age"
                name="age_by_dob"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === "") {
                        return Promise.resolve();
                      }
              
                      if (!Number.isInteger(value) || value < 0) {
                        return Promise.reject(
                          new Error("Age must be a positive integer")
                        );
                      }
              
                      return Promise.resolve();
                    },
                  },
                ]}
              >

                <InputNumber
                  min={0}
                  max={120}
                  precision={0}      
                  step={1}          
                  className={inputStyles.mdInput}
                  placeholder="Enter Age"
                  style={{ width: "180px" }}
                  onChange={(age) => {
                    if (Number.isInteger(age) && age >= 0) {
                      const year = dayjs().year() - age;
                      const dob = dayjs(`${year}-01-01`);
                      form.setFieldValue("dob", dob);
                    } else {
                      form.setFieldValue("dob", null);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (["-", "+", "e", ".", ",","/"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>

            </Col>
          </Row>

          <Form.Item
              label="Gender"
              name="gender"
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Select
                allowClear
                placeholder="Select Gender"
                className={inputStyles.mdSelect}
                style={{ width: "180px" }}
                options={genderKeys}
              />
            </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: "email", message: "Please enter a valid email" }]}
          >
            <Input
              placeholder="Enter email"
              className={inputStyles.mdInput}
              style={{ width: "300px" }}
            />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input.TextArea
              placeholder="Enter address"
              className={inputStyles.mdInput}
              style={{ width: "300px" }}
            />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              placeholder="Enter notes"
              className={inputStyles.mdInput}
              style={{ width: "300px" }}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default AddPatientModal;
